import React, { useState, useRef, useEffect } from 'react';
import { HiX, HiZoomIn, HiZoomOut, HiDownload, HiArrowsExpand, HiEye } from 'react-icons/hi';

/**
 * Optimized FilePreview - Simple preview component for images and PDFs
 */
const FilePreview = ({ src, alt, type, children, ...props }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const modalContentRef = useRef(null);

  // Detect file type
  const fileExtension = src?.split('.').pop()?.toLowerCase() || '';
  const detectedType = type || (
    ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(fileExtension) 
      ? 'image' 
      : fileExtension === 'pdf' ? 'pdf' : 'unknown'
  );

  // PDF viewer configurations
  const pdfViewers = [
    `https://mozilla.github.io/pdf.js/web/viewer.html?file=${encodeURIComponent(src)}`,
    `https://docs.google.com/gview?url=${encodeURIComponent(src)}&embedded=true`,
    `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(src)}`,
    `${src}#view=FitH&toolbar=0&navpanes=0&scrollbar=1`
  ];

  // Reset state when modal opens
  useEffect(() => {
    if (isModalOpen) {
      setZoom(1);
      setIsLoading(true);
      setHasError(false);
    }
  }, [isModalOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => e.key === 'Escape' && setIsModalOpen(false);
    if (isModalOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isModalOpen]);

  // Handlers
  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.25));
  const handleResetZoom = () => setZoom(1);
  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = src;
    link.download = alt || 'file';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // PDF iframe error handler with fallback chain
  const handlePdfError = (isFullScreen = false) => {
    const iframe = document.querySelector(`iframe[title="${alt || 'PDF Document'}"]`);
    if (!iframe) return;
    
    const currentIndex = pdfViewers.findIndex(viewer => iframe.src.includes(viewer.split('?')[0].split('/').pop()));
    const nextIndex = currentIndex + 1;
    
    if (nextIndex < pdfViewers.length) {
      iframe.src = pdfViewers[nextIndex];
    } else {
      setHasError(true);
    }
  };

  // Render PDF iframe
  const renderPdfIframe = (viewerIndex = 0, height = "400", fullScreen = false) => (
    <iframe
      src={pdfViewers[viewerIndex]}
      width="100%"
      height={fullScreen ? "100%" : height}
      className={`border-0 ${fullScreen ? 'rounded-lg' : ''}`}
      title={alt || 'PDF Document'}
      sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
      onLoad={() => setIsLoading(false)}
      onError={() => handlePdfError(fullScreen)}
    />
  );

  // Render loading/error states
  const renderLoadingState = () => isLoading && !hasError && (
    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
      <div className="text-center">
        <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
        <p className="text-sm text-gray-600 dark:text-gray-400">Loading PDF...</p>
      </div>
    </div>
  );

  const renderErrorState = () => hasError && (
    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
      <div className="text-center p-4">
        <div className="text-4xl mb-2">📄</div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">PDF Preview Unavailable</p>
        <div className="flex gap-2 justify-center">
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
          >
            View Full Screen
          </button>
          <button
            onClick={handleDownload}
            className="px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors text-sm"
          >
            Download
          </button>
        </div>
      </div>
    </div>
  );

  // Render modal header
  const renderModalHeader = (title, showZoom = false) => (
    <div className="absolute top-4 left-4 right-4 z-10 bg-black bg-opacity-50 rounded-lg p-3">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-white truncate">{title}</h3>
        <div className="flex items-center gap-2">
          {showZoom && (
            <>
              <button onClick={handleZoomOut} className="flex items-center justify-center w-8 h-8 text-white hover:bg-white hover:bg-opacity-20 rounded transition-all" title="Zoom out">
                <HiZoomOut className="w-4 h-4" />
              </button>
              <span className="text-white text-sm px-2">{Math.round(zoom * 100)}%</span>
              <button onClick={handleZoomIn} className="flex items-center justify-center w-8 h-8 text-white hover:bg-white hover:bg-opacity-20 rounded transition-all" title="Zoom in">
                <HiZoomIn className="w-4 h-4" />
              </button>
              <button onClick={handleResetZoom} className="text-white text-sm px-2 hover:bg-white hover:bg-opacity-20 rounded transition-all">
                Reset
              </button>
            </>
          )}
          <button onClick={handleDownload} className="flex items-center justify-center w-8 h-8 text-white hover:bg-white hover:bg-opacity-20 rounded transition-all" title="Download">
            <HiDownload className="w-4 h-4" />
          </button>
          <button onClick={() => setIsModalOpen(false)} className="flex items-center justify-center w-8 h-8 text-white hover:bg-white hover:bg-opacity-20 rounded transition-all">
            <HiX className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );

  // IMAGE COMPONENT
  if (detectedType === 'image') {
    return (
      <>
        <div className="pm-image-wrapper w-[85%] mx-auto  mt-2 rounded-xl border border-gray-100 dark:border-gray-700/20 shadow-sm overflow-hidden bg-white dark:bg-gray-900/30 backdrop-blur-sm">
          <div className="flex items-center justify-between p-3 border-b bg-light-surfacePrimary dark:bg-secondary-dark border-gray-100 dark:border-secondary-dark">
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">
              Image
            </span>
            <div className="flex items-center  gap-2">
              <button
                onClick={() => setIsModalOpen(true)}
                className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 rounded-md transition-all duration-200 ease-in-out"
                title="View Full Screen"
              >
                <HiArrowsExpand className="h-4 w-4" />
              </button>
              <button
                onClick={handleDownload}
                className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 rounded-md transition-all duration-200 ease-in-out"
                title="Download"
              >
                <HiDownload className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="px-7 py-5 flex items-center justify-center bg-white dark:bg-transparent">
            <img
              src={src}
              alt={alt}
              className="max-w-full md:max-w-xl h-auto max-h-80 object-contain rounded-xl border border-gray-200 dark:border-gray-600"
             
              {...props}
            />
          </div>
        </div>

        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90">
            <div className="relative max-w-full max-h-full p-4">
              {renderModalHeader(alt || 'Image Preview', true)}
              <div ref={modalContentRef} className="mt-16 max-w-full max-h-full overflow-auto flex items-center justify-center" style={{ maxHeight: 'calc(100vh - 8rem)' }}>
                <img
                  src={src}
                  alt={alt}
                  className="max-w-none transition-transform duration-200"
                  style={{ transform: `scale(${(0.5 * zoom)})`, transformOrigin: 'center center' }}
                />
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // PDF COMPONENT
  if (detectedType === 'pdf') {
    return (
      <>
        <div className="pdf-preview border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden max-w-full">
          <div className="pdf-header bg-gray-100 dark:bg-gray-700 p-3 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate overflow-hidden flex-1 mr-2 min-w-0" title={alt || 'PDF Preview'}>
              📄 {alt && alt.length > 50 ? `${alt.substring(0, 50)}...` : (alt || 'PDF Preview')}
            </span>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setIsModalOpen(true)}
                className="flex items-center gap-1 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                <HiArrowsExpand className="w-3 h-3" />
                Full Screen
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center gap-1 px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
              >
                <HiDownload className="w-3 h-3" />
                Download
              </button>
            </div>
          </div>
          
          <div className="relative">
            {renderPdfIframe()}
            {renderLoadingState()}
            {renderErrorState()}
          </div>
        </div>

        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
            <div className="relative w-full h-full max-w-7xl max-h-full p-4">
              <div className="absolute top-4 left-4 right-4 z-10 bg-white dark:bg-gray-800 rounded-lg p-4 shadow-lg">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate overflow-hidden flex-1 mr-4" title={alt || 'PDF Document'}>
                    📄 {alt || 'PDF Document'}
                  </h3>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={handleDownload}
                      className="flex items-center gap-1 px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                      <HiDownload className="w-4 h-4" />
                      Download
                    </button>
                    <button
                      onClick={() => setIsModalOpen(false)}
                      className="flex items-center justify-center w-8 h-8 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      <HiX className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="pt-20 h-full">
                {renderPdfIframe(1, "100%", true)}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // LINK BUTTON FOR PREVIEW
  if (children && (detectedType === 'pdf' || detectedType === 'image')) {
    return (
      <button
        onClick={() => setIsModalOpen(true)}
        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
        {...props}
      >
        <HiEye className="w-4 h-4" />
        {children}
      </button>
    );
  }

  // FALLBACK LINK
  return (
    <a 
      href={src} 
      target="_blank" 
      rel="noopener noreferrer"
      className="text-blue-600 dark:text-blue-400 hover:underline"
      {...props}
    >
      {children}
    </a>
  );
};

export default FilePreview; 